/**
 * Budget-constrained task router implementation.
 * Based on PILOT pattern (arXiv:2508.21141) for cost-efficient task routing.
 *
 * Routes tasks with respect to token, cost, and latency budgets.
 * Provides budget tracking, warnings, and enforcement options.
 *
 * @module cli-adapters/budget-router
 * (Source: Issue #102, arXiv:2508.21141 - EMNLP 2025)
 */

import type { Result } from '../core/index.js';
import { getTimeProvider } from '../core/index.js';
import { createLogger } from '../core/logger.js';
import type {
  IBudgetRouter,
  BudgetConstraint,
  SessionBudget,
  BudgetExceededError,
  BudgetWarning,
  BudgetRoutingResult,
  BudgetRouterOptions,
  CliTask,
  CliResponse,
  CliError,
  RoutingArmId,
  ICliAdapter,
} from './types.js';
import { DEFAULT_CAPABILITIES, routingArmDisplaySlot } from './types.js';
import type { CliName } from './types.js';
import type { BudgetCoverage } from './types-routing.js';
import { estimateTokens } from './budget-utils.js';
import { DEFAULT_COST_MODELS } from './budget-router-types.js';
import { generateBudgetWarnings } from './budget-warnings.js';
import { createBudgetExceededError } from './budget-errors.js';
import { detectTaskCategory } from '../config/task-specialization.js';
import { gatewayCostGap } from '../adapters/sdk/gateway-cost.js';
import {
  describeUnpricedArm,
  estimateArmCostUsd,
  estimateBudgetArmCostUsd,
} from './budget-arm-cost.js';

// The registry estimator moved to a sibling for the file cap (#4392 inc 2);
// re-exported so `budget-router` keeps its published surface.
export { estimateRegistryCostUsd } from './budget-arm-cost.js';

const logger = createLogger({ component: 'budget-router' });

/**
 * Default budget router configuration.
 */
const DEFAULT_OPTIONS: Required<BudgetRouterOptions> = {
  defaultConstraints: {
    maxTokens: 100000,
    maxCostUsd: 1.0,
    maxLatencyMs: 60000,
  },
  sessionBudget: {
    tokenBudget: 1000000,
    costBudgetUsd: 10.0,
    resetIntervalMs: 3600000, // 1 hour
  },
  warningThresholds: {
    info: 50,
    warning: 75,
    critical: 90,
  },
  enforceHardLimits: true,
  // #4196: per-task-class cost ceilings default OFF (no ceiling configured).
  taskClassCostCeilings: {},
};

/**
 * Profile latency for a routing slot, or `undefined` when the slot has no cost
 * model (#4907).
 *
 * `undefined` means unmeasured, and an unmeasured candidate is admitted rather
 * than rejected: a latency budget must not silently exclude every model whose
 * profile happens to be missing.
 */
function latencyOf(slot: CliName): number | undefined {
  return DEFAULT_COST_MODELS[slot]?.avgLatencyMs;
}

type UnpricedArm = NonNullable<BudgetRoutingResult['unpricedArms']>[number];

/**
 * What `selectAdapterWithinBudget` found (#6393): the first admissible arm
 * with its adapter, or `null`; plus every arm it could not price. The ARM id
 * travels with the adapter because a gateway adapter's display `name` is its
 * slot, and pricing the name is exactly the misreport this fixes.
 */
interface BudgetSelection {
  readonly selected: { readonly arm: RoutingArmId; readonly adapter: ICliAdapter } | null;
  readonly unpricedArms: readonly UnpricedArm[];
}

export class BudgetRouter implements IBudgetRouter {
  private readonly adapters: Map<RoutingArmId, ICliAdapter>;
  private readonly options: Required<BudgetRouterOptions>;
  private tokensUsed = 0;
  private costSpentUsd = 0;
  /**
   * Per-dimension debit counts (#5240). A budget accumulates a MIXTURE of
   * measured and estimated usage; without these a reader cannot tell which,
   * and `actualTokens` / `actualCostUsd` asserted a measurement they may not
   * have held.
   */
  private coverage = {
    measuredTokenDebits: 0,
    estimatedTokenDebits: 0,
    measuredCostDebits: 0,
    estimatedCostDebits: 0,
  };
  private sessionStartedAt: Date;
  private resetTimer?: ReturnType<typeof setTimeout>;

  constructor(adapters: Map<RoutingArmId, ICliAdapter>, options?: BudgetRouterOptions) {
    this.adapters = adapters;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.sessionStartedAt = new Date(getTimeProvider().now());

    // Set up auto-reset timer if configured
    const resetInterval = this.options.sessionBudget.resetIntervalMs ?? 3600000;
    if (resetInterval > 0) {
      this.scheduleReset();
    }
  }

  /**
   * Get current session budget status.
   */
  /**
   * A read-only snapshot of the debit basis counts (#5240).
   *
   * Copied rather than shared so a caller holding an old budget cannot see it
   * change under them, and typed as {@link BudgetCoverage} so the public shape
   * stays readonly while the internal accumulator remains mutable.
   */
  private snapshotCoverage(): BudgetCoverage {
    return { ...this.coverage };
  }

  getSessionBudget(): SessionBudget {
    const sessionBudget = this.options.sessionBudget;
    const tokenBudget = sessionBudget.tokenBudget ?? 1000000;
    const costBudgetUsd = sessionBudget.costBudgetUsd ?? 10.0;
    const resetIntervalMs = sessionBudget.resetIntervalMs ?? 3600000;

    const tokensRemaining = Math.max(0, tokenBudget - this.tokensUsed);
    const costRemainingUsd = Math.max(0, costBudgetUsd - this.costSpentUsd);

    const tokenUtilization = tokenBudget > 0 ? (this.tokensUsed / tokenBudget) * 100 : 0;
    const costUtilization = costBudgetUsd > 0 ? (this.costSpentUsd / costBudgetUsd) * 100 : 0;
    const utilizationPercent = Math.max(tokenUtilization, costUtilization);

    const resetsAt =
      resetIntervalMs > 0 ? new Date(this.sessionStartedAt.getTime() + resetIntervalMs) : undefined;

    const result: SessionBudget = {
      tokenBudget,
      costBudgetUsd,
      tokensUsed: this.tokensUsed,
      costSpentUsd: this.costSpentUsd,
      tokensRemaining,
      costRemainingUsd,
      utilizationPercent,
      coverage: this.snapshotCoverage(),
      startedAt: this.sessionStartedAt,
    };

    if (resetsAt !== undefined) {
      return { ...result, resetsAt };
    }
    return result;
  }

  /**
   * Update session budget after task completion.
   */
  updateBudget(usage: { tokens?: number; costUsd?: number }): void {
    if (usage.tokens !== undefined) {
      this.tokensUsed += usage.tokens;
    }
    if (usage.costUsd !== undefined) {
      this.costSpentUsd += usage.costUsd;
    }

    logger.debug('Budget updated', {
      tokensUsed: this.tokensUsed,
      costSpentUsd: this.costSpentUsd,
    });
  }

  /**
   * Reset session budget.
   */
  resetBudget(): void {
    this.tokensUsed = 0;
    this.costSpentUsd = 0;
    // A reset window has measured nothing; stale counts would let it claim
    // coverage it never had.
    this.coverage = {
      measuredTokenDebits: 0,
      estimatedTokenDebits: 0,
      measuredCostDebits: 0,
      estimatedCostDebits: 0,
    };
    this.sessionStartedAt = new Date(getTimeProvider().now());
    logger.info('Budget reset');

    // Reschedule reset timer
    const resetInterval = this.options.sessionBudget.resetIntervalMs ?? 3600000;
    if (resetInterval > 0) {
      this.scheduleReset();
    }
  }

  /**
   * Check if task is within budget constraints.
   */
  checkBudget(task: CliTask, constraint?: BudgetConstraint): BudgetRoutingResult {
    const budget = { ...this.options.defaultConstraints, ...constraint };

    // Estimate tokens for this task
    const estimatedInputTokens = estimateTokens(task.content);
    const estimatedOutputTokens = task.maxTokens ?? estimatedInputTokens * 2;
    const estimatedTokens = estimatedInputTokens + estimatedOutputTokens;

    // Find the best adapter within budget
    const { selected, unpricedArms } = this.selectAdapterWithinBudget(budget, estimatedTokens);
    const adapter = selected === null ? null : selected.adapter;
    // Priced by ARM, not `adapter.name` (#6393). A selected arm was priceable
    // at selection time with the same env, so `undefined` here is unreachable;
    // it is still handled as NOT within budget rather than as a number.
    const armCostUsd =
      selected === null
        ? undefined
        : estimateBudgetArmCostUsd(selected.arm, estimatedInputTokens, estimatedOutputTokens);
    // Named empty case: with nothing selectable there is no cost to report,
    // and `0` is the existing "nothing would run" value. It is never a
    // gateway's fabricated rate — that arm was skipped and is listed in
    // `unpricedArms`.
    const estimatedCostUsd = armCostUsd ?? 0;

    const estimatedLatencyMs =
      selected === null ? undefined : latencyOf(routingArmDisplaySlot(selected.arm));

    // Check budget constraints
    const currentBudget = this.getSessionBudget();
    const withinBudget =
      armCostUsd !== undefined && this.checkConstraints(budget, estimatedTokens, estimatedCostUsd);

    // Generate warnings
    const warnings = generateBudgetWarnings(
      currentBudget,
      estimatedTokens,
      estimatedCostUsd,
      this.options.warningThresholds
    );

    // Project budget after task
    const projectedBudget = this.projectBudget(estimatedTokens, estimatedCostUsd);

    return {
      adapter,
      withinBudget,
      estimatedCostUsd,
      estimatedTokens,
      ...(estimatedLatencyMs !== undefined ? { estimatedLatencyMs } : {}),
      ...(unpricedArms.length > 0 ? { unpricedArms } : {}),
      warnings,
      projectedBudget,
    };
  }

  /**
   * Per-task-class cost ceiling filter (#4196, epic #4175).
   *
   * Resolves the task's class via `detectTaskCategory`; when a ceiling is
   * configured for that class, each candidate's cost is estimated with
   * canonical registry pricing and candidates above the ceiling are dropped.
   *
   * BINDING fail direction: a candidate with MISSING registry pricing FAILS
   * the check (fail-closed) — unknown cost must not slip under a configured
   * ceiling. This is deliberately NOT the return-all-candidates fallback of
   * `filterByPreferenceTier` (composite-router-helpers.ts).
   *
   * Billing-mode gating (api only) is the caller's responsibility
   * (`applyBudgetFilter`); plan mode never invokes this.
   */
  filterByTaskClassCeiling(task: CliTask, candidates: RoutingArmId[]): RoutingArmId[] {
    const ceiling = this.resolveTaskClassCeiling(task);
    if (ceiling === undefined) return candidates;
    const inputTokens = estimateTokens(task.content);
    const outputTokens = task.maxTokens ?? inputTokens * 2;
    return candidates.filter((arm) => {
      // A vendor arm is priced by its display slot's default model (#3422); a
      // gateway arm by its NEXUS_GATEWAY_COST declaration (#4392 inc 2).
      const cost = estimateArmCostUsd(arm, inputTokens, outputTokens);
      if (cost === undefined) {
        const gap = gatewayCostGap(arm);
        if (gap !== undefined) {
          logger.warn(`Cost ceiling: gateway cost ${gap} — excluding (fail-closed)`, {
            arm,
            ceiling,
          });
        } else {
          logger.debug('Cost ceiling: missing registry pricing — failing closed', {
            arm,
            ceiling,
          });
        }
        return false;
      }
      const within = cost <= ceiling;
      if (!within) {
        logger.debug('Cost ceiling: candidate excluded', { arm, cost, ceiling });
      }
      return within;
    });
  }

  /** Resolve the configured ceiling for the task's detected class, if any (#4196). */
  private resolveTaskClassCeiling(task: CliTask): number | undefined {
    const ceilings = this.options.taskClassCostCeilings;
    if (Object.keys(ceilings).length === 0) return undefined;
    const match = detectTaskCategory(task.content);
    if (match === null) return undefined;
    return ceilings[match.category];
  }

  /**
   * Route task with budget awareness.
   */
  routeWithBudget(
    task: CliTask,
    budget?: BudgetConstraint
  ): Promise<Result<BudgetRoutingResult, BudgetExceededError>> {
    const result = this.checkBudget(task, budget);

    if (!result.withinBudget && this.options.enforceHardLimits) {
      const currentBudget = this.getSessionBudget();
      const error = createBudgetExceededError(
        budget ?? this.options.defaultConstraints,
        result,
        currentBudget
      );
      return Promise.resolve({ ok: false, error });
    }

    logger.debug('Budget routing decision', {
      withinBudget: result.withinBudget,
      adapter: result.adapter?.name,
      estimatedCost: result.estimatedCostUsd,
      warnings: result.warnings.length,
    });

    return Promise.resolve({ ok: true, value: result });
  }

  /**
   * Execute task with budget tracking.
   */
  async executeWithBudget(
    task: CliTask,
    budget?: BudgetConstraint
  ): Promise<Result<CliResponse & { budgetAfter: SessionBudget }, CliError>> {
    // First, route with budget check
    const routingResult = await this.routeWithBudget(task, budget);
    if (!routingResult.ok) {
      return routingResult;
    }

    const { adapter, estimatedTokens, estimatedCostUsd, warnings } = routingResult.value;
    if (!adapter) {
      return this.createNoAdapterError();
    }

    this.logWarnings(warnings);

    // Execute the task
    const result = await this.executeAndTrack(adapter, task, estimatedTokens, estimatedCostUsd);
    return result;
  }

  private createNoAdapterError(): Result<CliResponse & { budgetAfter: SessionBudget }, CliError> {
    return {
      ok: false,
      error: {
        code: 'NOT_FOUND',
        message: 'No adapter available within budget constraints',
        cli: 'claude',
        retryable: false,
      },
    };
  }

  private logWarnings(warnings: readonly BudgetWarning[]): void {
    for (const warning of warnings) {
      if (warning.level === 'critical') {
        logger.warn('Budget critical warning', { warning });
      } else {
        logger.debug('Budget warning', { warning });
      }
    }
  }

  private async executeAndTrack(
    adapter: ICliAdapter,
    task: CliTask,
    estimatedTokens: number,
    estimatedCostUsd: number
  ): Promise<Result<CliResponse & { budgetAfter: SessionBudget }, CliError>> {
    const startTime = getTimeProvider().now();
    const result = await adapter.execute(task);

    if (!result.ok) {
      return result;
    }

    // #5240: each figure is EITHER a measurement or this router's estimate, and
    // the two dimensions diverge — every vendor except Claude reports tokens and
    // no cost. `debited*` rather than `actual*` because neither name is right
    // for a field holding both; the basis travels in `coverage` instead.
    const measuredTokens = result.value.usage?.totalTokens;
    const measuredCostUsd = result.value.costUsd;
    const debitedTokens = measuredTokens ?? estimatedTokens;
    const debitedCostUsd = measuredCostUsd ?? estimatedCostUsd;

    if (measuredTokens === undefined) this.coverage.estimatedTokenDebits++;
    else this.coverage.measuredTokenDebits++;
    if (measuredCostUsd === undefined) this.coverage.estimatedCostDebits++;
    else this.coverage.measuredCostDebits++;

    this.updateBudget({ tokens: debitedTokens, costUsd: debitedCostUsd });

    const budgetAfter = this.getSessionBudget();
    const durationMs = getTimeProvider().now() - startTime;

    logger.info('Task executed with budget tracking', {
      adapter: adapter.name,
      debitedTokens,
      debitedCostUsd,
      tokensMeasured: measuredTokens !== undefined,
      costMeasured: measuredCostUsd !== undefined,
      durationMs,
      budgetUtilization: Math.round(budgetAfter.utilizationPercent),
    });

    return { ok: true, value: { ...result.value, durationMs, budgetAfter } };
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
    }
  }

  // Private helpers

  private scheduleReset(): void {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
    }
    const resetInterval = this.options.sessionBudget.resetIntervalMs ?? 3600000;
    this.resetTimer = setTimeout(() => {
      this.resetBudget();
    }, resetInterval);
  }

  private selectAdapterWithinBudget(
    budget: BudgetConstraint,
    estimatedTokens: number
  ): BudgetSelection {
    // Sort adapters by cost efficiency (higher = cheaper). Capabilities and
    // pricing are slot-level (DEFAULT_CAPABILITIES keyed by CliName); an api:*
    // arm uses its display slot's profile (#3422).
    const sortedAdapters = [...this.adapters].sort((a, b) => {
      const capA = DEFAULT_CAPABILITIES[routingArmDisplaySlot(a[0])];
      const capB = DEFAULT_CAPABILITIES[routingArmDisplaySlot(b[0])];
      return capB.cost - capA.cost; // Prefer cheaper models
    });
    const unpricedArms: UnpricedArm[] = [];

    for (const [arm, adapter] of sortedAdapters) {
      // A CLI slot or vendor arm keeps the conservative fallback; a gateway
      // arm is priced by its declaration and is `undefined` when it has none
      // (#6393). Unknown is NOT within budget: skip it and say why.
      const estimatedCost = estimateBudgetArmCostUsd(arm, estimatedTokens / 2, estimatedTokens / 2);
      if (estimatedCost === undefined) {
        const reason = describeUnpricedArm(arm);
        logger.warn(`Budget: ${reason} — not admitting ${arm} (fail-closed)`, { arm, reason });
        unpricedArms.push({ arm, reason });
        continue;
      }
      if (
        this.admitsCandidate(budget, estimatedTokens, routingArmDisplaySlot(arm), estimatedCost)
      ) {
        return { selected: { arm, adapter }, unpricedArms };
      }
    }

    return { selected: null, unpricedArms };
  }

  /** The four per-candidate admission checks of `selectAdapterWithinBudget`. */
  private admitsCandidate(
    budget: BudgetConstraint,
    estimatedTokens: number,
    slot: CliName,
    estimatedCost: number
  ): boolean {
    const caps = DEFAULT_CAPABILITIES[slot];
    const withinTokenBudget = budget.maxTokens === undefined || estimatedTokens <= budget.maxTokens;
    const withinCostBudget = budget.maxCostUsd === undefined || estimatedCost <= budget.maxCostUsd;
    const withinContextWindow = estimatedTokens <= caps.contextWindow;
    // #4907: the third declared budget. `maxLatencyMs` was validated,
    // defaulted and plumbed from routing YAML, but read by nothing, so the
    // `'latency'` violation kind had no producer and no input could make the
    // constraint bind.
    const withinLatencyBudget =
      budget.maxLatencyMs === undefined ||
      latencyOf(slot) === undefined ||
      (latencyOf(slot) as number) <= budget.maxLatencyMs;
    return withinTokenBudget && withinCostBudget && withinContextWindow && withinLatencyBudget;
  }

  private checkConstraints(
    budget: BudgetConstraint,
    estimatedTokens: number,
    estimatedCostUsd: number
  ): boolean {
    const currentBudget = this.getSessionBudget();

    // Check per-task constraints
    if (budget.maxTokens !== undefined && estimatedTokens > budget.maxTokens) {
      return false;
    }
    if (budget.maxCostUsd !== undefined && estimatedCostUsd > budget.maxCostUsd) {
      return false;
    }

    // Check session budget
    if (currentBudget.tokensRemaining < estimatedTokens) {
      return false;
    }
    if (currentBudget.costRemainingUsd < estimatedCostUsd) {
      return false;
    }

    return true;
  }

  private projectBudget(estimatedTokens: number, estimatedCostUsd: number): SessionBudget {
    const current = this.getSessionBudget();
    return {
      ...current,
      tokensUsed: current.tokensUsed + estimatedTokens,
      costSpentUsd: current.costSpentUsd + estimatedCostUsd,
      tokensRemaining: Math.max(0, current.tokensRemaining - estimatedTokens),
      costRemainingUsd: Math.max(0, current.costRemainingUsd - estimatedCostUsd),
      utilizationPercent: Math.max(
        ((current.tokensUsed + estimatedTokens) / current.tokenBudget) * 100,
        ((current.costSpentUsd + estimatedCostUsd) / current.costBudgetUsd) * 100
      ),
    };
  }
}

/** Create a budget router instance. */
export function createBudgetRouter(
  adapters: Map<RoutingArmId, ICliAdapter>,
  opts?: BudgetRouterOptions
): IBudgetRouter {
  return new BudgetRouter(adapters, opts);
}
